// Third-party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { SortingRule } from 'react-table';

// Components
import { PrimaryButton } from '../../../components/buttons';
import SearchInput from '../../../components/inputs/SearchInput';
import { EditPortfolioModal, DeletePortfoliosModal } from '../../../components/modals';
import { PortfoliosTable } from '../../../components/tables';
import { LoadingSpinner } from '../../../components/common';
import PortfolioUploadModal from '@/views/components/modals/portfolio-upload/PortfolioUploadModal';
import DropdownButton from '@/views/components/buttons/DropdownButton';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '../../../../enums/Portfolio';

// Utils
import { canAccessUploadCSV } from '@/utils/feature-access';
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { showError } from '../../../../utils/errors';
import { getPortfolioMissingHoldings } from '../../../../utils/portfolio/utils';
import { ENTERPRISE_GROUP_NAME } from '../../../../utils/enterprise';
import { userHasPermissionEnabled, PERMISSIONS } from '../../../../utils/permissions';
import { PortfolioEditorContext } from '@/utils/portfolios';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { getEnterprisePortfolios, deleteEnterpriseGroupPortfolios, saveEnterpriseGroupPortfolio } from '../../../../redux/modules/enterprise/admin/portfolios';
import { DELETE_ENTERPRISE_GROUP_PORTFOLIO } from '../../../../redux/modules/enterprise/admin/shared';
import { resetPortfolio, loadPortfolioForEdit, setPortfolioEnterprise, savePortfolio, setPortfolioEditorContext } from '@/redux/modules/edit-portfolio';
import { selectIsLoadingEvery } from '../../../../redux/modules/ui/loaders';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';
import EnterpriseAdminGroup from '@/types/enterprise-admin-group';
import GenericContact from '@/types/generic-contact';
import PaginatedPortfolios from '@/types/paginated-portfolios';
import { togglePortfolioEditor } from '@/redux/modules/common/actionCreators';

type State = {
  query: string;
  loading: boolean;
  showDeletePortfolioModal: boolean;
  selectedPortfolioForDelete?: Portfolio[];
  nextPortfoliosToken?: string;
  token?: string;
  sorting: SortingRule[];
  showPortfolioUploadModal: boolean;
};

type ParentProps = {
  user: User;
  group?: EnterpriseAdminGroup;
  className?: string;
};

type StateProps = {
  group: EnterpriseAdminGroup;
  isDeletingEnterprisePortfolio: boolean;
  showEditPortfolioModal: boolean;
};

type DispatchProps = {
  getEnterprisePortfolios: (groupId?: string, query?: string, next?: string, sorting?: SortingRule[]) => Promise<PaginatedPortfolios>;
  loadPortfolioForEdit: (portfolio: Portfolio) => Promise<void>;
  deleteEnterpriseGroupPortfolios: (groupId: string, portfolios: Portfolio[]) => Promise<void>;
  savePortfolio: (portfolio: Portfolio, contact?: GenericContact, enterpriseGroupId?: string) => Promise<Portfolio>;
  resetPortfolio: () => void;
  setPortfolioEnterprise: () => void;
  togglePortfolioEditor: (show: boolean) => void;
  setPortfolioEditorContext: (editorContext: PortfolioEditorContext) => void;
};

type Props = ParentProps & DispatchProps & StateProps;

class EnterprisePortfolios extends Component<Props, State> {
  private portfoliosTable = React.createRef<PortfoliosTable<Portfolio>>();

  readonly state: State = {
    query: '',
    loading: false,
    showDeletePortfolioModal: false,
    sorting: [],
    showPortfolioUploadModal: false,
  };

  loadPortfolios = debounce((reset = false) => {
    const { query, token, sorting } = this.state;
    const { group } = this.props;

    const localToken = reset ? undefined : token;
    this.setState({ loading: true });
    this.props.getEnterprisePortfolios(group._id, query, localToken, sorting).then(() => {
      this.setState({ loading: false });
    });
  }, 500);

  componentDidMount() {
    this.loadPortfolios();
  }

  resetPortfolio = () => {
    this.props.resetPortfolio();
    this.props.setPortfolioEnterprise();
  };

  onLoadFolioForEdit = (portfolio: Portfolio) => this.props.loadPortfolioForEdit(portfolio);

  onCreatePortfolio = () => {
    this.resetPortfolio();
    this.openEditPortfolioModal();
  };

  onEditPortfolio = (portfolio: Portfolio) => {
    this.props.setPortfolioEditorContext(PortfolioEditorContext.PORTFOLIO);
    this.props
      .loadPortfolioForEdit(portfolio)
      .then(() => {
        this.openEditPortfolioModal();
      })
      .catch(showError);
  };

  openEditPortfolioModal = () => {
    this.props.setPortfolioEditorContext(PortfolioEditorContext.PORTFOLIO);
    this.props.togglePortfolioEditor(true);
  };

  onCloseEditPortfolioModal = () => {
    this.props.resetPortfolio();
    this.props.togglePortfolioEditor(false);
  };

  onSavePortfolio = (portfolio: Portfolio): Promise<Portfolio> => {
    const { group } = this.props;

    return this.props.savePortfolio(portfolio, undefined, group._id).then(portfolio => {
      this.onCloseEditPortfolioModal();
      this.loadPortfolios(true);
      return portfolio;
    });
  };

  // Show the Delete Confirmation modal when the user tries to delete a portfolio
  onDelete = (portfolios: Portfolio[]) => {
    this.setState({ selectedPortfolioForDelete: portfolios });
    this.showDeletePortfolioModal();
  };

  onDeleteSelectedPortfolios = (portfolios: Portfolio[]) => {
    this.setState({ selectedPortfolioForDelete: portfolios });
    this.showDeletePortfolioModal();
  };

  showDeletePortfolioModal = () => {
    this.setState({ showDeletePortfolioModal: true });
  };

  hideDeletePortfolioModal = () => {
    this.setState({ showDeletePortfolioModal: false });
  };

  onConfirmDeletePortfolio = () => {
    const { group } = this.props;
    const portfolios = this.state.selectedPortfolioForDelete;

    if (!portfolios) {
      return;
    }

    // Reload enterprise's data after deleting a portfolio
    this.props.deleteEnterpriseGroupPortfolios(group._id, portfolios).then(() => {
      this.portfoliosTable.current?.unselectAll();
      this.hideDeletePortfolioModal();
      this.loadPortfolios(true);
    });
  };

  onCancelDeletePortfolio = () => {
    this.setState({ selectedPortfolioForDelete: undefined });
    this.hideDeletePortfolioModal();
  };

  onPortfoliosSearch = (query: string) => {
    this.setState({ query }, () => {
      this.loadPortfolios(true);
    });
  };

  onLoadMore = () => {
    this.loadPortfolios();
  };

  onSortingChange = (sorting: SortingRule[]) => {
    const { group } = this.props;
    this.setState({ sorting, loading: true });
    this.props.getEnterprisePortfolios(group._id, undefined, undefined, sorting).then(() => {
      this.setState({ loading: false });
    });
  };

  getDisplayedColumns = () => {
    const { user } = this.props;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  createDropdownItems = () => [
    {
      className: 'large-icon',
      node: formatMessage('Comparison.NewModel'),
      icon: 'create-portfolio1',
      onClick: this.onCreatePortfolio,
    },
    {
      className: 'large-icon',
      node: formatMessage('Comparison.UploadCSVFile'),
      icon: 'upload-file',
      onClick: () => this.setState({ showPortfolioUploadModal: true }),
      hidden: !canAccessUploadCSV(this.props.user),
    },
  ];

  render() {
    const { loading, showPortfolioUploadModal, showDeletePortfolioModal, selectedPortfolioForDelete } = this.state;
    const { group, isDeletingEnterprisePortfolio, showEditPortfolioModal } = this.props;

    const displayedColumns = this.getDisplayedColumns();
    const displayedMetrics = [PORTFOLIO_METRICS_FIELDS.MER, PORTFOLIO_METRICS_FIELDS.YEAR_ONE, PORTFOLIO_METRICS_FIELDS.YEAR_THREE];

    if (group == null || group.portfolios == null) {
      return null;
    }

    return (
      <div className={classNames(this.props.className, 'enterprise-portfolios')}>
        <div className="section-headers d-flex justify-content-between">
          <SearchInput
            className="settings-search"
            value={this.state.query}
            onChange={this.onPortfoliosSearch}
            placeholder={formatMessage('Admin.Enterprise.SearchPortfolios')}
          />
          <DropdownButton
            toggle={{
              component: PrimaryButton,
              dark: true,
              icon: 'plus-med',
              className: 'create-portfolios',
            }}
            alignRight
            items={this.createDropdownItems()}
          >
            <FormattedMessage id="Contacts.ContactTabs.Portfolios.Create" />
          </DropdownButton>
        </div>

        <PortfoliosTable<Portfolio>
          ref={this.portfoliosTable}
          className={classNames({ 'sr-only': loading })}
          portfolios={group.portfolios}
          displayedColumns={displayedColumns}
          displayedMetrics={displayedMetrics}
          onSort={this.onSortingChange}
          onDelete={this.onDelete}
          onEdit={this.onEditPortfolio}
          selectedTitle="PortfoliosTable.SelectedTitle.Models"
          missingProductPredicate={portfolio => getPortfolioMissingHoldings(portfolio).length > 0}
        />

        {loading && <LoadingSpinner />}

        <EditPortfolioModal show={showEditPortfolioModal} onSave={this.onSavePortfolio} onCancel={this.onCloseEditPortfolioModal} />

        <PortfolioUploadModal
          show={showPortfolioUploadModal}
          onCancel={() => this.setState({ showPortfolioUploadModal: false })}
          titleId="Modal.PortfolioUpload.UploadModelPortfolio"
        />

        <DeletePortfoliosModal
          isDeleting={isDeletingEnterprisePortfolio}
          portfolios={selectedPortfolioForDelete || []}
          show={showDeletePortfolioModal}
          onCancel={this.onCancelDeletePortfolio}
          onConfirm={this.onConfirmDeletePortfolio}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState, ownProps: ParentProps) => {
  let { group } = ownProps;

  if (!group) {
    const groups = state.enterprise.admin ? state.enterprise.admin.groups : [];
    group = groups.find(g => g.name === ENTERPRISE_GROUP_NAME) as EnterpriseAdminGroup;
  }

  return {
    group,
    isDeletingEnterprisePortfolio: selectIsLoadingEvery(state, [DELETE_ENTERPRISE_GROUP_PORTFOLIO.type]),
    showEditPortfolioModal: state.comparison.portfolioEditor.isVisible,
  };
};

export default connect(mapStateToProps, {
  deleteEnterpriseGroupPortfolios,
  getEnterprisePortfolios,
  loadPortfolioForEdit,
  resetPortfolio,
  saveEnterpriseGroupPortfolio,
  setPortfolioEnterprise,
  savePortfolio,
  togglePortfolioEditor,
  setPortfolioEditorContext,
})(EnterprisePortfolios);
